import { useEffect, useRef, useState } from "react"
import { useController } from "react-hook-form"
import IASelect from "./types"
import mergeDeep from "@mobi/utils/object/mergeDeep"

import throttle from "lodash.throttle"

const messages = {
  required: "Campo obrigatório",
  emptyMessage: "Nenhuma opção encontrada",
  isSearchingMessage: "buscando...",
  generalError: "Um erro ocorreu ao consultar os dados",
  minLength: (value) =>
    `digite ao menos ${value} caracteres para fazer a busca`,
  isValidValue: "Nenhuma opção encontrada",
}

const useSelect = ({
  control,
  name,
  minSearchCharacters = 3,
  openOnFocus = true,
  options: defaultOptions = [],
  isSearchingMessage = messages.isSearchingMessage,
  fetchOptions,
  filterOptions = ({ options, search }) => 
    options.filter((option) =>
      option.label
        .toString()
        .trim()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[^a-zA-Z\u00C0-\u00FF\d\s]+/gim, '')
        .includes(search
          .trim()
          .toLowerCase()
          .normalize("NFD")
          .replace(/[^a-zA-Z\u00C0-\u00FF\d\s]+/gim, '')
        )
    ),
  formProps,
  native,
  required = true,
  validators: { rules: propRules = {}, messages: propMessages = {} } = {},
  getInputValue: getInputValueProps,
  isReadOnly = false,
  clearOnFocus = true,
  noSearch = false,
  ...props
}: IASelect) => {
  const fieldId = `ds-field-${name}`

  const [errorMessage, setErrorMessage] = useState("")
  const [isFocused, setIsFocused] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const [isSelected, setIsSelected] = useState(false)
  const [options, setOptions]: Array<any> = useState([])
  const [isFirstListElement, setIsFirstListElement] = useState(true)

  const isValidValue = (conditionValue) =>
    !required ||
    !!fetchOptions ||
    defaultOptions.some((objItem: any) => objItem.label === conditionValue) ||
    options.length > 0

  const DEFAULT_RULES = {
    validate: {
      isValidValue: isValidValue,
    },
  }

  const rules = mergeDeep(DEFAULT_RULES, propRules, { required })
  const customMessages = mergeDeep(messages, propMessages)

  const { field, fieldState } = useController({ name, control, rules })
  const { error, isDirty, invalid, isTouched } = fieldState

  const hasInteraction = isDirty || isTouched
  const hasErrorMessage = hasInteraction && !!errorMessage
  const isEmptyList = options && options.length === 0
  const isValid = !hasInteraction || !invalid

  const selectedOptionIndex = options.findIndex(
    (opt: any) =>
      opt === field.value ||
      opt.value === field.value ||
      opt.label === field.value
  )

  useEffect(() => {
    return () => {
      throttledSearch.cancel()
    }
  }, [])

  useEffect(() => {
    if (native) setOptions(defaultOptions)
  }, [defaultOptions])

  useEffect(() => {
    if (!isDirty) return
    if (error) {
      setErrorMessage(customMessages[error?.type])
      return
    }

    setErrorMessage("")
  }, [error])

  const throttledSearch = useRef(
    throttle((search) => {
      setIsSearching(true)
      setErrorMessage(isSearchingMessage)

      fetchOptions({ search })
        .then((results = []) => {
          setOptions(results)
          setIsSearching(false)
          if (results.length === 0 || !results) {
            setErrorMessage(customMessages.emptyMessage)
          } else {
            setErrorMessage("")
          }
          formProps.trigger(name)
        })
        .catch(() => {
          setIsSearching(false)
          setErrorMessage(customMessages.generalError)
        })
    }, 500)
  ).current as any

  const searchAndSetMessages = (search) => {
    if (search.length >= minSearchCharacters) {
      throttledSearch(search)
    } else {
      formProps.setError(name, {
        type: "minLength",
        message: `digite ao menos ${minSearchCharacters} caracteres para fazer a busca`,
      })
    }

    if (search.length === 0) {
      setOptions([])
    }
  }

  const onCustomChange = (e) => {
    const search = e?.target?.value
    if (!native) {
      if (fetchOptions) {
        searchAndSetMessages(search)
      } else {
        const results = filterOptions({ options: defaultOptions, search })
        if (options.length < 1) {
          setErrorMessage(customMessages.emptyMessage)
        }
        setOptions(results)
      }
    }
    field.onChange(search)
    props.onChange && props.onChange(search)
  }

  const onSelectItem = (option, { isSelectionFinished = true } = {}) => {
    setIsSelected(true)
    setErrorMessage("")

    if (!native && isSelectionFinished) {
      setOptions([])
    }

    field.onChange(option.label)
    props.onChange && props.onChange(option)
    props.onSelectItem && props.onSelectItem(option)
  }

  const onFocus = (e) => {
    if (!isReadOnly && isReadOnly !== undefined) {
      if (!native) {
        setIsFocused(true)
        setIsSelected(false)
        clearOnFocus && formProps.setValue(name, "")
        setErrorMessage("")

        if (openOnFocus) {
          setOptions(defaultOptions)
        }
      }

      props?.onFocus && props?.onFocus(e)
    }
  }

  const onBlur = (e) => {
    if (!isReadOnly && isReadOnly !== undefined) {
      setIsFocused(false)
      if (e.relatedTarget && e.relatedTarget.className.includes(`result-list`))
        return false

      if (!native && openOnFocus) {
        setOptions([])
      }

      if (field.value && isSelected) {
        formProps.clearErrors(name)
        setErrorMessage("")
      }

      if (!field.value || !isSelected) {
        if (clearOnFocus) {
          formProps.setValue(name, "")
          if (required) {
            formProps.setError(name, {
              type: "required",
              message: customMessages.required,
            })
            setErrorMessage(customMessages.required)
          }
        }
      }

      if (
        !!field.value &&
        field.value.length < minSearchCharacters &&
        fetchOptions
      ) {
        formProps.setError(name, {
          type: "minLength",
          message: `digite ao menos ${minSearchCharacters} caracteres para fazer a busca`,
        })
      }

      if (
        !defaultOptions.find((objItem: any) => {
          return objItem.label === field.value
        }) &&
        !fetchOptions &&
        required
      ) {
        formProps.setError(name, {
          type: "emptyMessage",
          message: customMessages.emptyMessage,
        })
        setErrorMessage(customMessages.emptyMessage)
      }

      field.onBlur()
      props.onBlur && props.onBlur(e)
    }
  }

  const handleOpenSelectClick = (e) => {
    e.preventDefault()

    if (!isFocused) {
      const element: HTMLInputElement = document.querySelector(
        `input#ds-field-${name}`
      )
      element && element.focus()
      setOptions(defaultOptions)
      setIsFocused(true)
      return
    }
    setOptions([])
    setIsFocused(false)
  }

  const onKeyDown = (e) => {
    const key = e.key
    const listbox = document.querySelector(`#results-${fieldId}`)
    const field = document.querySelector(`#${fieldId}`) as HTMLElement
    const activeElementID = field.getAttribute("aria-activedescendant")
    const activeElement = listbox?.querySelector(`#${activeElementID}`)
    const activeCssClass = "ds-select__item--active"

    const isOpened = options.length > 0

    if (key === "ArrowDown" || key === "ArrowUp") {
      if (isOpened) {
        const isNextOrPrevious =
          (activeElement && activeElement.nextSibling) ||
          (activeElement && activeElement.previousSibling)

        const nextElement = (
          isNextOrPrevious && !isFirstListElement
            ? activeElement[
                key === "ArrowDown" ? "nextSibling" : "previousSibling"
              ]
            : activeElement
        ) as any

        if (nextElement) {
          const characters = [...listbox.children]
          field.setAttribute("aria-activedescendant", nextElement.id)
          characters.forEach((element: any) => {
            element.classList.remove(activeCssClass)
            element.setAttribute("aria-selected", false)
          })
          nextElement.setAttribute("aria-selected", true)
          nextElement.classList.add(activeCssClass)
          document.querySelector(`#option-list-${fieldId}`).scrollTop =
            nextElement.offsetTop - 120

          const nextItemOnArrow = nextElement?.id?.split("_")?.pop()
          nextItemOnArrow &&
            onSelectItem(options[nextItemOnArrow], {
              isSelectionFinished: false,
            })
        }
        setIsFirstListElement(false)
      } else {
        onFocus(e)
      }
    } else if (key === "Enter") {
      e.preventDefault()
      if (activeElement) {
        const selectedItemOnEnter = activeElement?.id?.split("_")?.pop()
        field.setAttribute("aria-activedescendant", `option-${fieldId}_0`)
        onSelectItem(options[selectedItemOnEnter])
      }
    }
  }

  const getInputValue = () => {
    if (!field.value || !getInputValueProps) {
      return field.value || ""
    }

    if (!isFocused) {
      return getInputValueProps(field.value)
    }

    if (!isSelected) {
      return field.value || ""
    }

    return getInputValueProps(field.value)
  }

  const shouldPlaceholderInvisible = () => {
    const hasValue = (data) => data !== "" && data !== null && data !== undefined
    return !hasValue(props.defaultValue) && !hasValue(field.value)
  }

  return {
    ...props,
    customMessages,
    error,
    errorMessage,
    field,
    fieldId,
    fieldState,
    handleOpenSelectClick,
    hasErrorMessage,
    isEmptyList,
    isSearching,
    isSelected,
    isFocused,
    isValid,
    onBlur,
    onCustomChange,
    onFocus,
    onKeyDown,
    onSelectItem,
    getInputValue,
    options,
    required,
    native,
    shouldPlaceholderInvisible,
    selectedOptionIndex
  }
}

export default useSelect
